/**
 * BookingCard Component
 *
 * Displays booking information for a specific date/employee.
 * Delegates to specialized sub-components based on view type.
 *
 * Ported from Vue BookingCard.vue
 *
 * Views:
 * - week: Full booking list with hours and project names (WeekDayCell)
 * - month: Compact cell showing total hours (MonthDayCell)
 * - year: Minimal cell showing date number (YearDayCell)
 *
 * @example
 * ```tsx
 * <BookingCard
 *   card={cardData}
 *   view="week"
 *   onClick={handleClick}
 * />
 * ```
 */

import { Badge } from "@/components/ui/display/Badge";
import { Tooltip } from "@/components/ui/feedback/Tooltip";
import React, { useCallback } from "react";

import { SumBadge } from "@/components/booking/display/SumBadge";
import { MonthDayCell } from "@/components/schedule/MonthDayCell";
import { WeekDayCell } from "@/components/schedule/WeekDayCell";
import { YearDayCell } from "@/components/schedule/YearDayCell";

import styles from "./BookingCard.module.css";
import type { BookingCardProps } from "./types";
import { useBookingCardState } from "./useBookingCardState";

export const BookingCard: React.FC<BookingCardProps> = React.memo(
  ({
    card,
    view = "month",
    colored = true,
    showDate = false,
    dayHours = 8,
    selected = false,
    selectable = false,
    disabled = false,
    inRangePreview = false,
    compact = false,
    onClick,
    onMouseEnter,
    className,
    "data-testid": testId,
  }) => {
    // Use extracted hook for all calculated state
    const {
      hours,
      status,
      absenceFill,
      cardIsToday,
      cardIsWeekend,
      cardIsRedDay,
      dateString,
      dateTooltipContent,
      bookingItems,
    } = useBookingCardState({ card, dayHours });

    // Handle click - passes event for modifier key detection (shift, ctrl, meta)
    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        if (!disabled && onClick) {
          onClick(e, card);
        }
      },
      [disabled, onClick, card],
    );

    // Handle mouse enter - for shift-hover range preview (Bug #9023)
    const handleMouseEnter = useCallback(
      (e: React.MouseEvent) => {
        if (!disabled && onMouseEnter) {
          onMouseEnter(e, card);
        }
      },
      [disabled, onMouseEnter, card],
    );

    // Determine layout mode for week view
    const weekLayout = compact ? "compact" : "horizontal";

    // Container class with optional custom className
    const containerClass = className
      ? styles.container + " " + className
      : styles.container;

    // Render based on view type
    switch (view) {
      case "week":
        // Compact layout: pills in a row above the card
        if (compact && showDate) {
          return (
            <div
              className={containerClass}
              data-testid={testId}
              data-layout="compact"
              onMouseEnter={onMouseEnter ? handleMouseEnter : undefined}
            >
              {/* Pills row - date on left, sum on right */}
              <div data-component-name="pills-row">
                <Tooltip
                  content={dateTooltipContent}
                  position="top"
                  delay={300}
                >
                  <Badge
                    variant={cardIsToday ? "dark" : "default"}
                    size="md"
                    data-component-name="date-label"
                    data-weekend={cardIsWeekend}
                    data-red-day={cardIsRedDay}
                    aria-label={dateTooltipContent}
                    data-testid="booking-card-date"
                  >
                    {dateString}
                  </Badge>
                </Tooltip>
                <SumBadge
                  hours={hours.totalHours}
                  status={status}
                  data-testid="booking-card-sum"
                />
              </div>

              {/* Card content - full width */}
              <WeekDayCell
                bookings={bookingItems}
                excludedHours={hours.excludedHours}
                status={status}
                colored={colored}
                isWeekend={cardIsWeekend}
                absenceFill={absenceFill}
                selected={selected}
                selectable={selectable}
                inRangePreview={inRangePreview}
                disabled={disabled}
                onClick={onClick ? handleClick : undefined}
                data-component-name="week-day-cell"
              />
            </div>
          );
        }

        // Standard horizontal layout: date pill | card | sum pill
        return (
          <div
            className={containerClass}
            data-testid={testId}
            data-layout={weekLayout}
            onMouseEnter={onMouseEnter ? handleMouseEnter : undefined}
          >
            {/* Date label */}
            {showDate && (
              <Tooltip content={dateTooltipContent} position="top" delay={300}>
                <Badge
                  variant={cardIsToday ? "dark" : "default"}
                  size="md"
                  data-component-name="date-label"
                  data-weekend={cardIsWeekend ? "true" : undefined}
                  data-red-day={cardIsRedDay ? "true" : undefined}
                  aria-label={dateTooltipContent}
                  data-testid="booking-card-date"
                >
                  {dateString}
                </Badge>
              </Tooltip>
            )}

            {/* Card content */}
            <WeekDayCell
              bookings={bookingItems}
              excludedHours={hours.excludedHours}
              status={status}
              colored={colored}
              isWeekend={cardIsWeekend}
              absenceFill={absenceFill}
              selected={selected}
              selectable={selectable}
              inRangePreview={inRangePreview}
              disabled={disabled}
              onClick={onClick ? handleClick : undefined}
              data-component-name="week-day-cell"
            />

            {/* Sum label */}
            {showDate && (
              <SumBadge
                hours={hours.totalHours}
                status={status}
                data-testid="booking-card-sum"
              />
            )}
          </div>
        );

      case "month":
        return (
          <div
            className={containerClass}
            data-testid={testId}
            data-layout="vertical"
            onMouseEnter={onMouseEnter ? handleMouseEnter : undefined}
          >
            {showDate && (
              <Badge
                variant={cardIsToday ? "dark" : "default"}
                size="md"
                data-component-name="date-label"
                data-weekend={cardIsWeekend ? "true" : undefined}
                data-red-day={cardIsRedDay ? "true" : undefined}
                aria-label={dateTooltipContent}
                data-testid="booking-card-date"
              >
                {dateString}
              </Badge>
            )}

            <MonthDayCell
              hours={hours.totalHours - hours.excludedHours}
              status={colored ? status : undefined}
              absenceFill={absenceFill}
              isWeekend={cardIsWeekend}
              selected={selected}
              selectable={selectable}
              inRangePreview={inRangePreview}
              disabled={disabled}
              onClick={onClick ? handleClick : undefined}
            />
          </div>
        );

      case "year":
        return (
          <div
            className={containerClass}
            data-testid={testId}
            data-layout="vertical"
            onMouseEnter={onMouseEnter ? handleMouseEnter : undefined}
          >
            <YearDayCell
              date={card.date}
              hours={hours.totalHours - hours.excludedHours}
              status={colored ? status : undefined}
              absenceFill={absenceFill}
              isToday={cardIsToday}
              isWeekend={cardIsWeekend}
              selected={selected}
              selectable={selectable}
              inRangePreview={inRangePreview}
              disabled={disabled}
              onClick={onClick ? handleClick : undefined}
            />
          </div>
        );
    }
  },
);

BookingCard.displayName = "BookingCard";

export default BookingCard;
